import { CountBadge, Tooltip } from '../ui';
import { badgeTotal, type BadgePart } from './navBadgeParts';

/**
 * A nav count that says what it is made of.
 *
 * A bare red pill states a quantity and nothing else, and the reader is left with three questions it
 * cannot answer: what work is this, why can't I see it on the page it opens, and what do I press to
 * make it go away. Half of these numbers are SUMS over unrelated queues, which makes all three
 * unanswerable by inspection — "Attendance 4" was two doubtful punches, one day missing a punch and
 * one fix request, cleared in three different places.
 *
 * So the pill carries its own breakdown on hover and focus: the total in the page's own words, then
 * one line per queue. The lines are the SAME numbers the pill is summed from (see `navBadgeParts`),
 * not a second description that has to be kept in step.
 *
 * The breakdown is deliberately a Tooltip and not a popover with links in it. This badge renders
 * INSIDE the nav link it belongs to, and an interactive control nested in an anchor is invalid markup
 * and unreachable by keyboard — the same trap the attendance tab badge fell into. The row's own link
 * is the actor: where a badge has exactly one queue behind it, the menu points that link at the
 * queue's slice, so pressing the thing you just read about lands on those rows.
 */
export function NavBadge({ parts, count, className }: {
  /** What the number is made of. Omitted (or empty) renders the plain pill — nothing to explain. */
  parts?: BadgePart[];
  /** Overrides the parts' sum. Only for counts with no breakdown, e.g. a group's children total. */
  count?: number;
  className?: string;
}) {
  const total = count ?? badgeTotal(parts);
  if (!total || total <= 0) return null;
  if (!parts?.length) return <CountBadge count={total} className={className} />;

  return (
    <Tooltip
      label={
        <span className="block">
          <span className="block font-semibold">
            {total} {total === 1 ? 'thing' : 'things'} waiting here
          </span>
          {parts.map((p) => (
            <span key={p.label} className="mt-1 block">
              <span className="tabular-nums font-semibold">{p.n}</span>{' '}
              {p.label}
              {/* Work you cannot clear by acting has to say so, or the reader hunts for a button
                  that does not exist on any screen they can open. */}
              {p.waiting && <span className="opacity-70"> — nothing for you to do yet</span>}
            </span>
          ))}
        </span>
      }
    >
      <CountBadge count={total} className={className} />
    </Tooltip>
  );
}
